/**
 * note-render.js — window.NoteRender / module.exports
 *
 * СПИСОК НОТ + ИНСТРУМЕНТ → ЗВУК. Общая дверь для всего, что у нас умеет
 * добывать ноты: распознавание из записи (basic-pitch → /audio-to-notes),
 * разбор партитуры (omr-pipeline → /sheet-music-to-midi), разбор .mid
 * (@tonejs/midi, TimbricaMidiParser) и то, что человек наиграл в /online-piano
 * или /virtual-drums.
 *
 * ── Почему прямой микс, а не граф Web Audio ───────────────────────────────
 * Замер 30.07.2026 на настоящем FluidR3 (4000 нот, 120 с) в /midi:
 *
 *     soundfont-player в один контекст …… >600 с (не закончил никогда)
 *     он же, по отрезкам ………………………………… 167 с
 *     прямой микс Float32 (этот код) …………  8.3 с
 *
 * soundfont-player — ЖИВОЙ проигрыватель, а не пакетный рендерер: узел на
 * ноту, и на тысячах нот граф не строится. Поэтому огибающая здесь —
 * аналитический двойник его же ADSR, а сэмплы складываются в Float32 напрямую.
 * Константы взяты из его умолчаний, а не подобраны на слух.
 *
 * ── Формат ноты ───────────────────────────────────────────────────────────
 * Принимаются ОБА домашних написания, потому что источники говорят по-разному:
 *   {pitchMidi, startTimeSeconds, durationSeconds, amplitude}  — basic-pitch, OMR
 *   {midi, time, duration, velocity}                           — @tonejs/midi
 * (та же терпимость, что у midi-file-writer.js::notesToEvents).
 *
 *   var inst = await NoteRender.load(ctx, ['violin'], { only: NoteRender.keysFor(notes) });
 *   var buf  = await NoteRender.render([{ instrument: 'violin', notes: notes }],
 *                                      { sampleRate: 44100, instruments: inst });
 *
 * ⚠️ Сэмплов у нас на диске 19 из 128 (оркестровый набор) — HOSTED. Просить
 * остальные бессмысленно: URL отдаст 404, и вызывающий получит тишину вместо
 * отказа. `isHosted()` существует, чтобы этот вопрос задавали ДО работы.
 *
 * ── Вживую: live() — тот же голос в настоящий AudioContext ────────────────
 *   var p = NoteRender.live(ctx, voices, { drumKit: kit, gain: 1 });
 *   p.start(0); … p.stop() → позиция; p.setGain(v); p.setVoiceGain(i, v); p.dispose();
 *
 * Те же партии и те же «работы» (jobsOf), что у render(): сэмпл, слой ударных,
 * огибающая, громкость и панорама — одни, поэтому прослушивание звучит как
 * файл не по совпадению двух копий, а по построению.
 *
 * ⚠️⚠️ Нота становится узлами за LOOKAHEAD до звучания, а не при нажатии
 * Play. Замер 21.10.2026 (жалоба /ru/midi «не услышал ничего», 7229 нот,
 * 469 с): прежний /midi ставил в контекст ВСЕ ноты сразу через
 * soundfont-player — 10 узлов на ноту, 63 тыс. узлов — и часы контекста шли
 * со скоростью 0.06–0.10 от настоящих (у слабой машины 0.02): звучало 0 с из
 * 30. Ожидающие узлы душат поток звука сами по себе, ещё не зазвучав. Тот же
 * файл через live(): 1.00, звучит 29 с из 30. Голос — 2 узла: soundfont-player
 * с упреждением держал 81 тыс. нот (500 в секунду) на 0.61, этот — на 0.995.
 * Разбор — docs/live-note-playback.md.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.NoteRender = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Имена General MIDI по номеру программы. ⚠️ Это ТРЕТЬЯ копия таблицы в
       репозитории (midi.blade.php::GM_INSTRUMENTS, midi-humanizer/audio.js::
       GM_NAMES) — и единственная, живущая в общем модуле. Новые вызывающие
       берут её отсюда. */
    var GM = ('acoustic_grand_piano,bright_acoustic_piano,electric_grand_piano,honkytonk_piano,electric_piano_1,'
        + 'electric_piano_2,harpsichord,clavinet,celesta,glockenspiel,music_box,vibraphone,marimba,xylophone,'
        + 'tubular_bells,dulcimer,drawbar_organ,percussive_organ,rock_organ,church_organ,reed_organ,accordion,'
        + 'harmonica,tango_accordion,acoustic_guitar_nylon,acoustic_guitar_steel,electric_guitar_jazz,'
        + 'electric_guitar_clean,electric_guitar_muted,overdriven_guitar,distortion_guitar,guitar_harmonics,'
        + 'acoustic_bass,electric_bass_finger,electric_bass_pick,fretless_bass,slap_bass_1,slap_bass_2,'
        + 'synth_bass_1,synth_bass_2,violin,viola,cello,contrabass,tremolo_strings,pizzicato_strings,'
        + 'orchestral_harp,timpani,string_ensemble_1,string_ensemble_2,synth_strings_1,synth_strings_2,'
        + 'choir_aahs,voice_oohs,synth_choir,orchestra_hit,trumpet,trombone,tuba,muted_trumpet,french_horn,'
        + 'brass_section,synth_brass_1,synth_brass_2,soprano_sax,alto_sax,tenor_sax,baritone_sax,oboe,'
        + 'english_horn,bassoon,clarinet,piccolo,flute,recorder,pan_flute,blown_bottle,shakuhachi,whistle,'
        + 'ocarina,lead_1_square,lead_2_sawtooth,lead_3_calliope,lead_4_chiff,lead_5_charang,lead_6_voice,'
        + 'lead_7_fifths,lead_8_bass__lead,pad_1_new_age,pad_2_warm,pad_3_polysynth,pad_4_choir,pad_5_bowed,'
        + 'pad_6_metallic,pad_7_halo,pad_8_sweep,fx_1_rain,fx_2_soundtrack,fx_3_crystal,fx_4_atmosphere,'
        + 'fx_5_brightness,fx_6_goblins,fx_7_echoes,fx_8_scifi,sitar,banjo,shamisen,koto,kalimba,bagpipe,'
        + 'fiddle,shanai,tinkle_bell,agogo,steel_drums,woodblock,taiko_drum,melodic_tom,synth_drum,'
        + 'reverse_cymbal,guitar_fret_noise,breath_noise,seashore,bird_tweet,telephone_ring,helicopter,'
        + 'applause,gunshot').split(',');

    /* Что физически лежит в public/vendor/midi-soundfonts/FluidR3_GM. Список
       РУЧНОЙ намеренно: он описывает содержимое диска, и сторож сверяет его с
       каталогом — иначе «инструмент есть» означало бы 404 и тишину. */
    var HOSTED = [
        'acoustic_grand_piano', 'bassoon', 'cello', 'clarinet', 'contrabass', 'flute',
        'french_horn', 'harpsichord', 'oboe', 'orchestral_harp', 'piccolo',
        'pizzicato_strings', 'string_ensemble_1', 'timpani', 'trombone', 'trumpet',
        'tuba', 'viola', 'violin'
    ];

    var BASE = '/vendor/midi-soundfonts';

    /* Огибающая: умолчания soundfont-player плюс постоянные времени из
       adsr-package. Не трогать «на слух» — это двойник чужого проигрывателя,
       и расхождение слышно как другой инструмент. */
    var ATT = 0.01, DEC = 0.1, SUS = 0.9, REL = 0.3;

    var FLATS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
    var SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

    function isHosted(name) { return HOSTED.indexOf(name) >= 0; }
    function programOf(name) { var i = GM.indexOf(name); return i < 0 ? 0 : i; }
    function nameOf(program) { return GM[((program | 0) % 128 + 128) % 128]; }

    /**
     * Одна нота в единое внутреннее написание. Принимает оба домашних формата;
     * возвращает null, если ноты в ней нет.
     */
    function one(n) {
        if (!n) return null;
        var midi = (typeof n.pitchMidi === 'number') ? n.pitchMidi : n.midi;
        if (typeof midi !== 'number' || !isFinite(midi)) return null;
        var t = (typeof n.startTimeSeconds === 'number') ? n.startTimeSeconds : n.time;
        var d = (typeof n.durationSeconds === 'number') ? n.durationSeconds : n.duration;
        var v = (typeof n.amplitude === 'number') ? n.amplitude : n.velocity;
        if (typeof t !== 'number' || !isFinite(t) || t < 0) return null;
        return {
            midi: Math.round(midi),
            /* Имя ноты («A#3») — запасной ключ буфера: часть наборов подписана
               им, а не номером. Приходит от @tonejs/midi, у остальных
               источников его нет, и это нормально. */
            name: (typeof n.name === 'string') ? n.name : null,
            time: t,
            duration: (typeof d === 'number' && isFinite(d) && d > 0) ? d : 0.25,
            velocity: (typeof v === 'number' && isFinite(v)) ? Math.max(0, Math.min(1, v)) : 0.8
        };
    }

    function normalize(notes) {
        var out = [];
        for (var i = 0; i < (notes || []).length; i++) { var n = one(notes[i]); if (n) out.push(n); }
        out.sort(function (a, b) { return a.time - b.time; });
        return out;
    }

    /**
     * Ключи сэмплов, которые нужны этим нотам — в ОБОИХ написаниях.
     * ⚠️ Файлы midi.js подписаны БЕМОЛЯМИ («Bb3»), а @tonejs/midi отдаёт диезы
     * («A#3»). Просить только диезы значит не совпасть ни с одним ключом и
     * получить ПОЛНОСТЬЮ ТИХИЙ выход — без единой ошибки. Лишние имена
     * загрузчик игнорирует, поэтому просим оба.
     */
    function keysFor(notes) {
        var set = Object.create(null), out = [];
        var ns = normalize(notes);
        for (var i = 0; i < ns.length; i++) {
            var m = ns[i].midi, oct = Math.floor(m / 12) - 1;
            var a = FLATS[m % 12] + oct, b = SHARPS[m % 12] + oct;
            if (!set[a]) { set[a] = 1; out.push(a); }
            if (!set[b]) { set[b] = 1; out.push(b); }
        }
        return out;
    }

    /** Естественная длина набора партий — до конца последней ноты. */
    function duration(voices) {
        var end = 0;
        for (var v = 0; v < (voices || []).length; v++) {
            var ns = normalize(voices[v].notes);
            for (var i = 0; i < ns.length; i++) {
                var e = ns[i].time + ns[i].duration;
                if (e > end) end = e;
            }
        }
        return end;
    }

    /**
     * Загрузка сэмплов. `only` резко сокращает работу: инструмент FluidR3 несёт
     * все 88 клавиш (~97 МБ разжатого PCM), а партия берёт пару десятков.
     */
    function load(ctx, names, opts) {
        opts = opts || {};
        var base = opts.base || BASE;
        var Sf = (typeof self !== 'undefined' && self.Soundfont) || (typeof window !== 'undefined' && window.Soundfont);
        if (!Sf) return Promise.reject(new Error('soundfont_player_missing'));

        var uniq = [], seen = Object.create(null);
        for (var i = 0; i < (names || []).length; i++) {
            var n = names[i];
            if (!n || seen[n]) continue;
            seen[n] = 1;
            if (!isHosted(n)) return Promise.reject(new Error('instrument_not_hosted:' + n));
            uniq.push(n);
        }
        if (!uniq.length) return Promise.resolve({});

        var nameToUrl = function (n, sf, fmt) {
            return base + '/FluidR3_GM/' + n + '-' + (fmt === 'ogg' ? 'ogg' : 'mp3') + '.js';
        };
        var done = 0, out = {};
        return Promise.all(uniq.map(function (n) {
            var o = { soundfont: 'FluidR3_GM', format: 'mp3', nameToUrl: nameToUrl };
            if (opts.only && opts.only.length) o.only = opts.only;
            return Sf.instrument(ctx, n, o).then(function (inst) {
                out[n] = inst;
                done++;
                if (typeof opts.onProgress === 'function') { try { opts.onProgress(done / uniq.length); } catch (_) {} }
            });
        })).then(function () { return out; });
    }

    /**
     * Партии → плоский список «работ»: что звучит, каким сэмплом, с какой
     * громкостью партии. Один на render() и live() — так файл и прослушивание
     * не могут разойтись в выборе сэмпла или слоя ударных.
     *
     * Слой ударных (`pickLayer`) берётся по очереди ОТДЕЛЬНО для каждой клавиши
     * набора. Общий на дорожку счётчик (как было до 21.10.2026) на рисунке
     * «бочка — хэт — бочка — хэт» отдавал бочке только чётные номера, хэту
     * только нечётные: при двух сэмплах на инструмент каждый всегда звучал
     * одним и тем же, и чередование, ради которого их два, не работало.
     *
     * opts.includeMuted — оставить заглушённые партии (live() глушит их своей
     * шиной и может вернуть звук без перепланирования); `vi` — номер партии.
     */
    function jobsOf(voices, opts) {
        opts = opts || {};
        var instruments = opts.instruments || {};
        var drumKit = opts.drumKit || null;
        var picker = (typeof self !== 'undefined' && self.GmDrumKit) || (typeof window !== 'undefined' && window.GmDrumKit);
        var jobs = [];
        for (var vi = 0; vi < (voices || []).length; vi++) {
            var voice = voices[vi];
            if (!voice || (voice.muted && !opts.includeMuted)) continue;
            var ns = normalize(voice.notes);
            var gain = (typeof voice.gain === 'number') ? voice.gain : 1;
            if (typeof voice.play === 'function') {
                for (var ci = 0; ci < ns.length; ci++) jobs.push({ note: ns[ci], play: voice.play, gain: gain, vi: vi });
                continue;
            }
            if (voice.percussion) {
                if (!drumKit || !picker) continue;
                var seq = Object.create(null);
                for (var pi = 0; pi < ns.length; pi++) {
                    var key = ns[pi].midi, hit = drumKit[key];
                    if (!hit) continue;
                    var nth = seq[key] || 0;
                    seq[key] = nth + 1;
                    var buf = picker.pickLayer(hit, ns[pi].velocity, nth);
                    if (buf) jobs.push({ note: ns[pi], oneShot: hit, oneShotBuf: buf, gain: gain, vi: vi });
                }
                continue;
            }
            var bufs = voice.buffers;
            if (!bufs) {
                var inst = instruments[voice.instrument];
                bufs = inst && inst.buffers;
            }
            if (!bufs) continue;
            for (var ni = 0; ni < ns.length; ni++) jobs.push({ note: ns[ni], buffers: bufs, gain: gain, vi: vi });
        }
        return jobs;
    }

    /* ── Живой голос ─────────────────────────────────────────────────────────
       Постоянные времени — те же, что у микса ниже (ln(t+1)/ln(100) пакета adsr). */
    var DEC_TC = Math.log(DEC + 1) / Math.log(100);       // 0.0207 с
    var REL_TC = Math.log(REL + 1) / Math.log(100);       // 0.0570 с
    /* Микс обрывает хвост, когда огибающая падает ниже 0.0005 (−66 дБ):
       живой голос гаснет там же, а не на 0.9 с позже. */
    var TAIL = REL_TC * Math.log(1 / 0.0005);              // ≈ 0.433 с
    var LOOKAHEAD = 1.0;      // с: нота становится узлами за столько до звучания
    var TICK_MS = 50;
    /* Потолок нот, живущих в графе одновременно (звучащие + ждущие). 1000 нот
       по 2 узла держат реальное время (замер 21.10); выше потолка нота ждёт
       своей очереди и, опоздав совсем, пропадает одна — а не весь звук. */
    var MAX_PENDING = 1500;
    var START_DELAY = 0.05;

    /** Огибающая (доля пика) через `el` с после начала ноты длиной `dur` — та же кривая, что пишет микс. */
    function envAt(el, dur) {
        var d = Math.max(0.01, dur);
        if (el <= 0) return 0;
        if (el < ATT) return el / ATT;
        if (el < d) return SUS + (1 - SUS) * Math.exp(-(el - ATT) / DEC_TC);
        return (SUS + (1 - SUS) * Math.exp(-(d - ATT) / DEC_TC)) * Math.exp(-(el - d) / REL_TC);
    }

    /**
     * План огибающей живого голоса — события AudioParam. `ts` — начало ноты в
     * часах контекста, `at` ≥ ts — когда звук правда начинается (нота,
     * опоздавшая или начатая до паузы, входит на ходу), `peak` — пик.
     * Чистая функция: её сверяет с миксом tests/js/note-render-live.test.mjs.
     */
    function envelopePlan(ts, at, dur, peak) {
        var d = Math.max(0.01, dur), attEnd = ts + ATT, rel = ts + d, el = at - ts, ev = [];
        if (el <= 0) {
            ev.push(['set', 0, ts], ['ramp', peak, attEnd]);
            if (rel > attEnd + 1e-6) ev.push(['target', peak * SUS, attEnd, DEC_TC]);
            ev.push(['target', 0, rel, REL_TC]);
        } else {
            ev.push(['set', peak * envAt(el, d), at]);
            if (el < ATT) {
                ev.push(['ramp', peak, attEnd]);
                if (rel > attEnd + 1e-6) ev.push(['target', peak * SUS, attEnd, DEC_TC]);
                ev.push(['target', 0, rel, REL_TC]);
            } else if (el < d) {
                ev.push(['target', peak * SUS, at, DEC_TC], ['target', 0, rel, REL_TC]);
            } else {
                ev.push(['target', 0, at, REL_TC]);
            }
        }
        return { events: ev, end: rel + TAIL };
    }

    function applyPlan(param, plan) {
        for (var i = 0; i < plan.events.length; i++) {
            var e = plan.events[i];
            if (e[0] === 'set') param.setValueAtTime(e[1], e[2]);
            else if (e[0] === 'ramp') param.linearRampToValueAtTime(e[1], e[2]);
            else param.setTargetAtTime(e[1], e[2], e[3]);
        }
    }

    /* Удар с панорамой — тот же расчёт, что в миксе (равномощная, ×1.414), но
       один раз на буфер и сторону, а не на каждый удар. */
    var pannedCache = (typeof WeakMap === 'function') ? new WeakMap() : null;
    function pannedOneShot(ctx, buf, pan) {
        pan = Math.max(-1, Math.min(1, pan || 0));
        var byPan = pannedCache && pannedCache.get(buf);
        if (!byPan) { byPan = {}; if (pannedCache) pannedCache.set(buf, byPan); }
        var k = String(pan);
        if (byPan[k]) return byPan[k];
        var ang = (pan + 1) * Math.PI / 4, gl = Math.cos(ang) * 1.414, gr = Math.sin(ang) * 1.414;
        var out = ctx.createBuffer(2, buf.length, buf.sampleRate);
        var d0 = buf.getChannelData(0), d1 = buf.numberOfChannels > 1 ? buf.getChannelData(1) : d0;
        var L = out.getChannelData(0), R = out.getChannelData(1);
        for (var i = 0; i < buf.length; i++) { L[i] = d0[i] * gl; R[i] = d1[i] * gr; }
        return (byPan[k] = out);
    }

    function defaultClock(fn, ms) {
        var bg = (typeof window !== 'undefined') && window.__audioBackground;
        /* Таймер в воркере: таймер главного потока в скрытой вкладке Chromium
           будит раз в минуту, и упреждение в секунду там означает тишину. */
        if (bg && typeof bg.createClock === 'function') return bg.createClock(fn, ms);
        var id = setInterval(fn, ms);
        return { stop: function () { clearInterval(id); } };
    }

    function nowMs() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }

    /**
     * Живое воспроизведение партий. Возвращает проигрыватель:
     *   start(offsetSec[, atCtx]) · stop() → позиция · position() · setGain(v) ·
     *   setVoiceGain(vi, v) · stats() · dispose() · playing
     * opts: { destination, instruments, drumKit, gain, lookaheadSec, maxPending,
     *         clock(fn, ms) → {stop}, onEnded(), onHealth(stats) }
     *
     * Партия может принести свой голос — `play(ctx, dest, note, at, skipSec)`
     * → {src, g, end} (так /midi играет синусом, когда сэмплов нет); часы,
     * упреждение и остановка остаются здесь.
     */
    function live(ctx, voices, opts) {
        opts = opts || {};
        var LOOK = opts.lookaheadSec > 0 ? opts.lookaheadSec : LOOKAHEAD;
        var CAP = opts.maxPending > 0 ? opts.maxPending : MAX_PENDING;
        var clockFn = typeof opts.clock === 'function' ? opts.clock : defaultClock;
        var queue = jobsOf(voices, { instruments: opts.instruments, drumKit: opts.drumKit, includeMuted: true });
        /* По времени; на равных — в порядке партий (sort стабилен с ES2019). */
        queue.sort(function (a, b) { return a.note.time - b.note.time; });

        var master = ctx.createGain();
        master.gain.value = (typeof opts.gain === 'number') ? opts.gain : 1;
        master.connect(opts.destination || ctx.destination);
        var buses = [];
        for (var vi = 0; vi < (voices || []).length; vi++) {
            var v = voices[vi] || {};
            var bus = ctx.createGain();
            bus.gain.value = v.muted ? 0 : ((typeof v.gain === 'number') ? v.gain : 1);
            bus.connect(master);
            buses.push(bus);
        }

        var playing = false, disposed = false, base = 0, qi = 0, pending = [];
        var clock = null, hold = null, lastPos = 0, endedSent = false;
        var st = { scheduled: 0, late: 0, dropped: 0, peakPending: 0, maxTickMs: 0, rt: null };
        var hv = null, healthSent = false;

        function endOf(job, ts) {
            if (job.oneShot) return ts + job.oneShotBuf.duration;
            if (job.play) return ts + job.note.duration + 1;
            return ts + Math.max(0.01, job.note.duration) + TAIL;
        }

        function voiceFor(job, ts, at) {
            var n = job.note, dest = buses[job.vi] || master;
            if (job.play) return job.play(ctx, dest, n, at, Math.max(0, at - ts));
            /* Сэмпл стартует на целом отсчёте, как в миксе. С дробного момента
               Chromium читает буфер с линейной интерполяцией — это фильтр:
               полсэмпла сдвига = −2.4 дБ на 10 кГц, −6 дБ на 15 кГц, и хэт с
               тарелкой вживую звучали на 0.5–2.6 дБ тише, чем в файле (замер
               21.10.2026 на FluidR3 + наборе ударных; мелодия — 0.02 дБ). */
            /* Оба момента — на сетку: иначе вовремя поставленная нота получала
               смещение в доли отсчёта ВНУТРИ буфера, и интерполяция возвращалась
               через него (у хэта −0.8 дБ). Опоздавшая входит на целое число
               отсчётов. */
            var sr = ctx.sampleRate;
            ts = Math.round(ts * sr) / sr;
            at = Math.round(at * sr) / sr;
            if (at < ts) at = ts;
            var skip = at - ts;
            var src = ctx.createBufferSource(), g = ctx.createGain();
            if (job.oneShot) {
                var dg = n.velocity * job.oneShot.gain * 0.8;
                if (!(dg > 0)) return null;
                src.buffer = pannedOneShot(ctx, job.oneShotBuf, job.oneShot.pan);
                g.gain.value = dg;
                src.connect(g); g.connect(dest);
                src.start(at, Math.min(skip, src.buffer.duration));
                return { src: src, g: g, end: ts + src.buffer.duration };
            }
            /* soundfont-player подписывает буферы номером ноты СТРОКОЙ. */
            var buf = job.buffers[n.midi] || job.buffers[String(n.midi)] || (n.name ? job.buffers[n.name] : null);
            var peak = n.velocity * 0.8;
            if (!buf || !(peak > 0) || skip >= buf.duration) return null;
            var plan = envelopePlan(ts, at, n.duration, peak);
            var end = Math.min(plan.end, ts + buf.duration);
            src.buffer = buf;
            src.connect(g); g.connect(dest);
            applyPlan(g.gain, plan);
            src.start(at, skip);
            src.stop(end);
            return { src: src, g: g, end: end };
        }

        function release(h) {
            try { h.src.disconnect(); } catch (_) { }
            try { h.g.disconnect(); } catch (_) { }
        }

        /* `joining` — нота, начатая до точки старта: входит на ходу и не
           считается опоздавшей (опоздание — это стоявший главный поток). */
        function schedule(job, now, joining) {
            var ts = base + job.note.time, at = ts;
            if (at < now) {
                if (endOf(job, ts) <= now + 0.005) { if (!joining) { st.late++; st.dropped++; } return; }
                if (!joining) st.late++;
                at = now;
            }
            var h = null;
            try { h = voiceFor(job, ts, at); } catch (_) { h = null; }
            if (!h) return;
            h.at = at;
            pending.push(h);
            st.scheduled++;
        }

        function tick() {
            if (!playing) return;
            var w0 = nowMs(), now = ctx.currentTime, k = 0, i;
            for (i = 0; i < pending.length; i++) {
                var h = pending[i];
                if (h.end < now - 0.05) release(h); else pending[k++] = h;
            }
            pending.length = k;
            /* Спящий контекст планировать можно: его часы стоят, окно вперёд
               не растёт, а поставленное зазвучит при пробуждении в свой срок. */
            var horizon = now + LOOK;
            while (qi < queue.length && pending.length < CAP) {
                var job = queue[qi];
                if (base + job.note.time >= horizon) break;
                qi++;
                schedule(job, now);
            }
            if (pending.length > st.peakPending) st.peakPending = pending.length;
            var spent = nowMs() - w0;
            if (spent > st.maxTickMs) st.maxTickMs = spent;
            measure(now, w0);
            if (qi >= queue.length && !pending.length && !endedSent) {
                endedSent = true;
                lastPos = position();
                halt();
                if (typeof opts.onEnded === 'function') { try { opts.onEnded(); } catch (_) { } }
            }
        }

        /* Идут ли часы контекста в ногу с настоящими — то, что человек слышит,
           а не то, что мы поставили. Меряется только на видимой вкладке и
           работающем контексте, раз за запуск, за первые 6 с. */
        function measure(now, w) {
            if (healthSent || typeof opts.onHealth !== 'function') return;
            var visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
            if (!visible || ctx.state !== 'running') { hv = null; return; }
            if (!hv) { hv = { ct: now, w: w }; return; }
            if (w - hv.w < 6000) return;
            st.rt = Math.round(((now - hv.ct) / ((w - hv.w) / 1000)) * 1000) / 1000;
            healthSent = true;
            try { opts.onHealth(stats()); } catch (_) { }
        }

        function lag() {
            var ac = (typeof window !== 'undefined') && window.AudibleClock;
            try { return ac ? ac.lagS(ctx) : 0; } catch (_) { return 0; }
        }

        function position() {
            if (!playing) return lastPos;
            return Math.max(0, ctx.currentTime - lag() - base);
        }

        function halt() {
            playing = false;
            if (clock) { try { clock.stop(); } catch (_) { } clock = null; }
            if (hold) { try { hold.release(); } catch (_) { } hold = null; }
        }

        function silenceAll() {
            var now = ctx.currentTime, sounding = [];
            for (var i = 0; i < pending.length; i++) {
                var h = pending[i];
                if (h.at > now + 0.01) {
                    /* Ещё не звучала — выключить и вынуть из графа сразу: одного
                       stop() мало, узел остаётся в графе до своего времени, и
                       при тысячах таких «пауза» душила следующий Play. */
                    try { h.src.stop(); } catch (_) { }
                    release(h);
                    continue;
                }
                try {
                    var p = h.g.gain;
                    if (typeof p.cancelAndHoldAtTime === 'function') p.cancelAndHoldAtTime(now);
                    else { p.cancelScheduledValues(now); p.setValueAtTime(p.value, now); }
                    p.setTargetAtTime(0, now, 0.008);
                    h.src.stop(now + 0.06);
                } catch (_) { }
                sounding.push(h);
            }
            pending = [];
            if (sounding.length) setTimeout(function () { sounding.forEach(release); }, 150);
        }

        /* atCtx — момент в часах контекста, когда звучит точка offsetSec; его
           передаёт тул, у которого своя «эпоха» (плейхед считается от неё). */
        function start(offsetSec, atCtx) {
            if (disposed) return;
            if (playing) stop();
            var off = Math.max(0, offsetSec || 0);
            var now = ctx.currentTime;
            base = ((typeof atCtx === 'number' && atCtx >= now) ? atCtx : now + START_DELAY) - off;
            endedSent = false; healthSent = false; hv = null;
            /* Ноты, начавшиеся до точки старта и ещё звучащие (педаль, пэд),
               входят на ходу, а не молчат до следующей своей ноты. */
            qi = 0;
            while (qi < queue.length && queue[qi].note.time < off) qi++;
            playing = true;
            for (var i = 0; i < qi; i++) {
                if (endOf(queue[i], base + queue[i].note.time) > now + START_DELAY) schedule(queue[i], now + START_DELAY, true);
            }
            var bg = (typeof window !== 'undefined') && window.__audioBackground;
            if (bg && typeof bg.holdScheduled === 'function') hold = bg.holdScheduled(ctx);
            clock = clockFn(tick, opts.tickMs || TICK_MS);
            tick();
        }

        function stop() {
            if (!playing) return lastPos;
            lastPos = position();
            halt();
            silenceAll();
            return lastPos;
        }

        function stats() {
            return { scheduled: st.scheduled, late: st.late, dropped: st.dropped, peakPending: st.peakPending,
                maxTickMs: Math.round(st.maxTickMs * 10) / 10, rt: st.rt, notes: queue.length };
        }

        function ramp(param, v) {
            var now = ctx.currentTime;
            try { param.cancelScheduledValues(now); param.setTargetAtTime(v, now, 0.01); }
            catch (_) { param.value = v; }
        }

        return {
            ctx: ctx,
            start: start,
            stop: stop,
            position: position,
            stats: stats,
            get playing() { return playing; },
            setGain: function (v) { ramp(master.gain, v); },
            setVoiceGain: function (i, v) { if (buses[i]) ramp(buses[i].gain, v); },
            dispose: function () {
                stop();
                disposed = true;
                setTimeout(function () {
                    try { master.disconnect(); } catch (_) { }
                    buses.forEach(function (b) { try { b.disconnect(); } catch (_) { } });
                }, 200);
            }
        };
    }

    /**
     * Партии → один стереофонический буфер.
     *
     * voices: [{ instrument: 'violin', notes: [...] }]
     *         [{ buffers: <уже загруженные сэмплы>, notes: [...] }]
     *         [{ percussion: true, notes: [...] }]  — играется набором ударных
     *
     * `buffers` напрямую — для вызывающего, у которого сэмплы уже на руках и
     * своя логика их подбора (так /midi отдаёт сюда программу из самого файла,
     * не спрашивая нас про имена).
     *
     * Ударные складываются ПЛОСКО: у одиночного удара своя огибающая внутри
     * сэмпла, и прогонять его через ADSR сэмплера значит заново атаковать то,
     * что уже отзвучало.
     */
    function render(voices, opts) {
        opts = opts || {};
        var sampleRate = opts.sampleRate || 44100;
        var dur = (typeof opts.durationSec === 'number' && opts.durationSec > 0)
            ? opts.durationSec
            : duration(voices) + REL * 3;
        var frames = Math.max(1, Math.ceil(sampleRate * dur));
        var instruments = opts.instruments || {};
        var drumKit = opts.drumKit || null;
        var signal = opts.signal || null;
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;

        var attF = Math.max(1, ATT * sampleRate);
        var decTc = (Math.log(DEC + 1) / Math.log(100)) * sampleRate;
        var relTc = (Math.log(REL + 1) / Math.log(100)) * sampleRate;
        /* Рекуррентные коэффициенты — одно умножение на сэмпл вместо Math.exp
           (замер: внутренний цикл втрое быстрее). */
        var decK = Math.exp(-1 / decTc);
        var relK = Math.exp(-1 / relTc);
        var RELEASE_TAIL = Math.ceil(REL * 3 * sampleRate);

        var jobs = jobsOf(voices, { instruments: instruments, drumKit: drumKit });

        var L = new Float32Array(frames);
        var R = new Float32Array(frames);
        var Ctor = (typeof AudioBuffer === 'function') ? AudioBuffer : null;
        var finish = function () {
            if (!Ctor) return { length: frames, sampleRate: sampleRate, L: L, R: R };
            var out = new Ctor({ numberOfChannels: 2, length: frames, sampleRate: sampleRate });
            out.copyToChannel(L, 0);
            out.copyToChannel(R, 1);
            return out;
        };
        if (!jobs.length) return Promise.resolve(finish());

        var YIELD_EVERY = 400;
        var ji = 0;
        function step() {
            var budget = 0;
            while (ji < jobs.length) {
                if (signal && signal.aborted) throw new DOMException('Render aborted', 'AbortError');
                mixOne(jobs[ji]);
                ji++;
                if (++budget >= YIELD_EVERY) break;
            }
            if (onProgress) { try { onProgress(ji / jobs.length); } catch (_) {} }
            if (ji >= jobs.length) return Promise.resolve(finish());
            return new Promise(function (r) { setTimeout(r, 0); }).then(step);
        }

        function mixOne(job) {
            var note = job.note;
            if (job.play) return;   // свой голос партии живёт только в live()
            if (job.oneShot) {
                var dstart = Math.round(note.time * sampleRate);
                if (dstart >= frames) return;
                var dbuf = job.oneShotBuf;
                var dg = note.velocity * job.oneShot.gain * 0.8 * job.gain;
                if (dg <= 0) return;
                var d0 = dbuf.getChannelData(0);
                var d1 = dbuf.numberOfChannels > 1 ? dbuf.getChannelData(1) : d0;
                /* Равномощная панорама: жёстко отведённый хэт не должен просесть. */
                var pan = Math.max(-1, Math.min(1, job.oneShot.pan || 0));
                var ang = (pan + 1) * Math.PI / 4;
                var gl = Math.cos(ang), gr = Math.sin(ang);
                var dtotal = Math.min(dbuf.length, frames - dstart);
                for (var i = 0; i < dtotal; i++) {
                    L[dstart + i] += d0[i] * dg * gl * 1.414;
                    R[dstart + i] += d1[i] * dg * gr * 1.414;
                }
                return;
            }

            /* soundfont-player подписывает буферы номером ноты СТРОКОЙ. */
            var buf = job.buffers[note.midi] || job.buffers[String(note.midi)]
                || (note.name ? job.buffers[note.name] : null);
            if (!buf) return;
            var start = Math.round(note.time * sampleRate);
            if (start >= frames) return;
            var g = note.velocity * 0.8 * job.gain;
            if (g <= 0) return;

            var src0 = buf.getChannelData(0);
            var src1 = buf.numberOfChannels > 1 ? buf.getChannelData(1) : src0;
            var relStartF = Math.max(0.01, note.duration) * sampleRate;
            var total = Math.min(buf.length, Math.ceil(relStartF) + RELEASE_TAIL, frames - start);

            var env = 0, decEnv = 1, relEnv = 1, released = false, envAtRelease = 1;
            for (var k = 0; k < total; k++) {
                if (k < attF) {
                    env = k / attF;
                } else if (k < relStartF) {
                    decEnv *= decK;
                    env = SUS + (1 - SUS) * decEnv;
                } else {
                    if (!released) { released = true; envAtRelease = env; relEnv = 1; }
                    relEnv *= relK;
                    env = envAtRelease * relEnv;
                    if (env < 0.0005) break;   // неслышный хвост — дальше не считаем
                }
                var e = env * g;
                L[start + k] += src0[k] * e;
                R[start + k] += src1[k] * e;
            }
        }

        return Promise.resolve().then(step);
    }

    return {
        GM: GM,
        HOSTED: HOSTED,
        BASE: BASE,
        isHosted: isHosted,
        programOf: programOf,
        nameOf: nameOf,
        normalize: normalize,
        keysFor: keysFor,
        duration: duration,
        load: load,
        render: render,
        jobsOf: jobsOf,
        live: live,
        envAt: envAt,
        envelopePlan: envelopePlan,
        TAIL: TAIL
    };
});
